import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Divider,
} from '@mui/material'
import {
  ContentCopy,
  ArrowBack,
  CheckCircle,
  SelectAll,
  DeselectOutlined,
} from '@mui/icons-material'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { IStores } from 'src/Stores'
import {
  QuotePotentialMatch,
  GetCopySourceLineItems,
  CopySourceLineItemSummary,
  ConsumerQuoteLineItemRef,
} from 'src/services/QuoteService'
import { formatCurrency } from 'src/utils/Formatter'
import { CostSourceText } from './CostSourceText'
import queryString from 'query-string'
import {
  processAction,
  CopyQuoteFromSourceViaWorkflow,
} from 'src/services/WorkflowService'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'

interface ICopyQuoteProps {
  targetQuoteId?: string
  quotePotentialMatches?: QuotePotentialMatch[]
  isLoadingPotentialMatches?: boolean
  getQuotePotentialMatches?: (quoteId: string) => Promise<void>
  rerouteToPath?: (path: string) => void
  loadQuote?: (quoteId: string, reload?: boolean) => Promise<void>
}

type LineItemType = 'PC' | 'TC' | 'AN' | 'PT' | 'MI'

const LINE_ITEM_TYPE_NAMES: Record<LineItemType, string> = {
  PC: 'Professional Components',
  TC: 'Technical Components',
  AN: 'Anesthesia',
  PT: 'Physical Therapy',
  MI: 'Miscellaneous',
}

const CopyQuote: React.FC<ICopyQuoteProps> = ({
  targetQuoteId,
  quotePotentialMatches = [],
  isLoadingPotentialMatches = false,
  getQuotePotentialMatches,
  rerouteToPath,
  loadQuote,
}) => {
  const { quoteId } = useParams<{ quoteId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const params = queryString.parse(location.search)
  const inboxItemId = params['inboxItemId']?.toString()
  const actionId = params['actionId']?.toString()
  const inboxId = params['inboxId'] ? Number(params['inboxId']) : undefined
  const isWorkflowAction = !!(inboxItemId && actionId)

  const [loadingSourceItems, setLoadingSourceItems] = useState(false)
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [selectedSourceQuote, setSelectedSourceQuote] =
    useState<QuotePotentialMatch | null>(null)
  const [sourceLineItems, setSourceLineItems] = useState<CopySourceLineItemSummary[]>([])
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<LineItemType>('PC')

  const currentRequestRef = React.useRef<string | null>(null)
  const loadingRequestRef = React.useRef<Promise<void> | null>(null)

  const effectiveQuoteId = targetQuoteId || quoteId

  useEffect(() => {
    if (effectiveQuoteId && getQuotePotentialMatches) {
      getQuotePotentialMatches(effectiveQuoteId)
    }
  }, [effectiveQuoteId, getQuotePotentialMatches])

  useEffect(() => {
    return () => {
      currentRequestRef.current = null
      loadingRequestRef.current = null
    }
  }, [])

  const groupedItems = useMemo(() => {
    const groups: Record<LineItemType, CopySourceLineItemSummary[]> = {
      PC: [],
      TC: [],
      AN: [],
      PT: [],
      MI: [],
    }

    sourceLineItems.forEach((item) => {
      if (item.type && groups[item.type]) {
        groups[item.type].push(item)
      }
    })

    return groups
  }, [sourceLineItems])

  const totalQuotedCost = sourceLineItems
    .filter((item) => selectedLineItemIds.has(item.id) && item.effectiveCost == null)
    .reduce((sum, item) => sum + (item.cost ?? 0), 0)

  const totalReconciledCost = sourceLineItems
    .filter((item) => selectedLineItemIds.has(item.id) && item.effectiveCost != null)
    .reduce((sum, item) => sum + item.effectiveCost!, 0)

  const hasQuotedOnlyItems = sourceLineItems.some(
    (item) => selectedLineItemIds.has(item.id) && item.effectiveCost == null
  )

  const hasReconciledItems = sourceLineItems.some(
    (item) => selectedLineItemIds.has(item.id) && item.effectiveCost != null
  )

  const handleSelectSource = async (sourceQuote: QuotePotentialMatch) => {
    if (loadingSourceItems) {
      return
    }

    if (loadingRequestRef.current) {
      try {
        await loadingRequestRef.current
      } catch {}
    }

    const requestId = `${sourceQuote.id}-${Date.now()}`
    currentRequestRef.current = requestId

    setSelectedSourceQuote(sourceQuote)
    setError(null)
    setSuccessMessage(null)
    setLoadingSourceItems(true)
    setSourceLineItems([])
    setSelectedLineItemIds(new Set())
    setActiveTab('PC')

    const executeRequest = async () => {
      try {
        const response = await GetCopySourceLineItems(sourceQuote.id)

        if (currentRequestRef.current === requestId) {
          setSourceLineItems(response.lineItems)

          const firstTabWithItems = (
            ['PC', 'TC', 'AN', 'PT', 'MI'] as LineItemType[]
          ).find((type) => response.lineItems.some((item) => item.type === type))
          if (firstTabWithItems) {
            setActiveTab(firstTabWithItems)
          }
          setLoadingSourceItems(false)
        }
      } catch (err: any) {
        if (currentRequestRef.current === requestId) {
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load line items. Please try again.'

          if (errorMessage.includes('DbContext') || errorMessage.includes('threading')) {
            setError('Server is busy processing. Please wait a moment and try again.')
          } else {
            setError(errorMessage)
          }
          setLoadingSourceItems(false)
        }
      } finally {
        loadingRequestRef.current = null
      }
    }

    const requestPromise = executeRequest()
    loadingRequestRef.current = requestPromise
    await requestPromise
  }

  const handleClearSelection = () => {
    currentRequestRef.current = null
    loadingRequestRef.current = null

    setSelectedSourceQuote(null)
    setSourceLineItems([])
    setSelectedLineItemIds(new Set())
    setActiveTab('PC')
    setError(null)
    setLoadingSourceItems(false)
  }

  const handleToggleItem = (itemId: string) => {
    setSelectedLineItemIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleToggleAllInTab = () => {
    const itemsInTab = groupedItems[activeTab]
    const allSelected = itemsInTab.every((item) => selectedLineItemIds.has(item.id))

    setSelectedLineItemIds((prev) => {
      const newSet = new Set(prev)
      if (allSelected) {
        itemsInTab.forEach((item) => newSet.delete(item.id))
      } else {
        itemsInTab.forEach((item) => newSet.add(item.id))
      }
      return newSet
    })
  }

  const handleCopy = async () => {
    if (!effectiveQuoteId || !selectedSourceQuote || selectedLineItemIds.size === 0) {
      return
    }

    setCopying(true)
    setError(null)

    try {
      const lineItemRefs: ConsumerQuoteLineItemRef[] = sourceLineItems
        .filter((item) => selectedLineItemIds.has(item.id))
        .map((item) => ({
          id: item.id,
          type: item.type,
        }))

      const copyRequest = {
        sourceQuoteId: selectedSourceQuote.id,
        lineItemIds: lineItemRefs,
        assignedClaimIds: [],
      }

      if (isWorkflowAction && inboxItemId && actionId) {
        const data = new ObjectiveProcessAction()
        data.copyQuoteFromSourceRequest = copyRequest

        await processAction(data, actionId, inboxItemId)

        if (location?.state?.from) {
          navigate(location.state.from)
        } else {
          navigate(-1)
        }
      } else if (inboxId) {
        await CopyQuoteFromSourceViaWorkflow(effectiveQuoteId, inboxId, copyRequest)

        if (loadQuote) {
          await loadQuote(effectiveQuoteId, true)
        }

        setSuccessMessage(
          `Successfully copied ${selectedLineItemIds.size} line item${
            selectedLineItemIds.size === 1 ? '' : 's'
          } from ${selectedSourceQuote.quoteMatch}`
        )

        handleClearSelection()
      } else {
        throw new Error('Copy Quote requires an inbox context (inboxId is required)')
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to copy line items. Please try again.'
      )
    } finally {
      setCopying(false)
    }
  }

  const handleBack = () => {
    if (rerouteToPath && effectiveQuoteId) {
      rerouteToPath(`/workflow/quote/${effectiveQuoteId}/default-inbox`)
    } else {
      navigate(-1)
    }
  }

  const allInTabSelected = useMemo(() => {
    const itemsInTab = groupedItems[activeTab]
    return (
      itemsInTab.length > 0 &&
      itemsInTab.every((item) => selectedLineItemIds.has(item.id))
    )
  }, [groupedItems, activeTab, selectedLineItemIds])

  if (isLoadingPotentialMatches) {
    return (
      <Box>
        <ResponsiveAppBar title="Copy Quote" pageIcon={<ContentCopy />} />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <ResponsiveAppBar title="Copy Quote" pageIcon={<ContentCopy />} />

      <Box p={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            variant="contained"
            size="small"
          >
            Back to Quote
          </Button>

          {selectedSourceQuote && (
            <Button
              variant="contained"
              size="small"
              onClick={handleClearSelection}
              disabled={copying}
            >
              Clear Selection
            </Button>
          )}
        </Box>

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            icon={<CheckCircle />}
            onClose={() => setSuccessMessage(null)}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {successMessage}
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Left Column: Source Quotes */}
          <Grid item xs={12} md={selectedSourceQuote ? 4 : 12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Quotes
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {selectedSourceQuote
                    ? 'Select a different quote to view its line items'
                    : 'Select a quote to view and copy its line items'}
                </Typography>

                {quotePotentialMatches.length === 0 ? (
                  <Alert severity="info">No quotes available to copy from.</Alert>
                ) : (
                  <List disablePadding>
                    {quotePotentialMatches.map((quote, index) => (
                      <React.Fragment key={quote.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          disablePadding
                          sx={{
                            bgcolor:
                              selectedSourceQuote?.id === quote.id
                                ? 'action.selected'
                                : 'transparent',
                            opacity:
                              loadingSourceItems && selectedSourceQuote?.id !== quote.id
                                ? 0.5
                                : 1,
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleSelectSource(quote)}
                            disabled={loadingSourceItems || copying}
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle2" fontWeight="medium">
                                    {quote.quoteMatch}
                                  </Typography>
                                  <Chip
                                    label={quote.matchStatus}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  {loadingSourceItems &&
                                    selectedSourceQuote?.id === quote.id && (
                                      <CircularProgress size={16} />
                                    )}
                                </Box>
                              }
                              secondary={
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>Surgery:</strong>{' '}
                                    {quote.matchSurgeryProviderGroup || 'N/A'}
                                  </Typography>
                                  {quote.matchFacilityProviderGroup && (
                                    <Typography variant="caption" color="text.secondary">
                                      <strong>Facility:</strong>{' '}
                                      {quote.matchFacilityProviderGroup}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>CPTs:</strong> {quote.matchingCPTs || 'N/A'}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Line Items (only shown when a source is selected) */}
          {selectedSourceQuote && (
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6">
                        Line Items from {selectedSourceQuote.quoteMatch}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select items to copy to your quote
                      </Typography>
                    </Box>
                    {selectedLineItemIds.size > 0 && (
                      <Chip
                        label={`${selectedLineItemIds.size} selected`}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>

                  {loadingSourceItems ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 2 }}
                      >
                        {(Object.keys(LINE_ITEM_TYPE_NAMES) as LineItemType[]).map(
                          (type) => {
                            const count = groupedItems[type].length
                            return (
                              <Tab
                                key={type}
                                label={
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <span>{LINE_ITEM_TYPE_NAMES[type]}</span>
                                    <Chip
                                      label={count}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.75rem',
                                      }}
                                    />
                                  </Box>
                                }
                                value={type}
                                disabled={count === 0}
                              />
                            )
                          }
                        )}
                      </Tabs>

                      {groupedItems[activeTab].length === 0 ? (
                        <Alert severity="info">
                          No {LINE_ITEM_TYPE_NAMES[activeTab]} line items available
                        </Alert>
                      ) : (
                        <>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Button
                              size="small"
                              variant="text"
                              startIcon={
                                allInTabSelected ? <DeselectOutlined /> : <SelectAll />
                              }
                              onClick={handleToggleAllInTab}
                            >
                              {allInTabSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              {
                                groupedItems[activeTab].filter((item) =>
                                  selectedLineItemIds.has(item.id)
                                ).length
                              }{' '}
                              / {groupedItems[activeTab].length} items
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              maxHeight: 'calc(100vh - 450px)',
                              minHeight: '200px',
                              overflowY: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <List disablePadding>
                              {groupedItems[activeTab].map((item, index) => {
                                const isSelected = selectedLineItemIds.has(item.id)
                                return (
                                  <React.Fragment key={item.id}>
                                    {index > 0 && <Divider />}
                                    <ListItem
                                      disablePadding
                                      sx={{
                                        bgcolor: isSelected
                                          ? 'primary.50'
                                          : 'transparent',
                                        borderLeft: isSelected ? 3 : 0,
                                        borderColor: 'primary.main',
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <ListItemButton
                                        onClick={() => handleToggleItem(item.id)}
                                      >
                                        <Checkbox
                                          edge="start"
                                          checked={isSelected}
                                          tabIndex={-1}
                                          disableRipple
                                          sx={{ mr: 1 }}
                                        />
                                        <ListItemText
                                          primary={
                                            <Box
                                              display="flex"
                                              alignItems="center"
                                              gap={1}
                                              flexWrap="wrap"
                                            >
                                              <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                              >
                                                {item.providerName || 'N/A'}
                                              </Typography>
                                              {item.cptCode && (
                                                <Chip
                                                  label={item.cptCode}
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              )}
                                              {item.typeText && (
                                                <Chip
                                                  label={item.typeText}
                                                  size="small"
                                                />
                                              )}
                                            </Box>
                                          }
                                          secondary={
                                            <Stack
                                              direction="row"
                                              spacing={2}
                                              sx={{ mt: 0.5 }}
                                            >
                                              {item.physicianName && (
                                                <Typography variant="caption">
                                                  <strong>MD:</strong>{' '}
                                                  {item.physicianName}
                                                </Typography>
                                              )}
                                              {item.costSource != null && (
                                                <Typography variant="caption">
                                                  <strong>Source:</strong>{' '}
                                                  {CostSourceText[item.costSource]}
                                                </Typography>
                                              )}
                                              {item.numberOfVisits && (
                                                <Typography variant="caption">
                                                  <strong>Visits:</strong>{' '}
                                                  {item.numberOfVisits}
                                                </Typography>
                                              )}
                                              <Typography variant="caption">
                                                <strong>Billed Charges:</strong>{' '}
                                                {formatCurrency(
                                                  (item.actualCost || 0).toString()
                                                )}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="primary.main"
                                                fontWeight="medium"
                                              >
                                                <strong>Quoted:</strong>{' '}
                                                {formatCurrency(
                                                  (item.cost || 0).toString()
                                                )}
                                              </Typography>
                                              {item.reconciledCost != null && (
                                                <Typography
                                                  variant="caption"
                                                  color="success.main"
                                                  fontWeight="medium"
                                                >
                                                  <strong>Reconciled:</strong>{' '}
                                                  {formatCurrency(
                                                    item.reconciledCost.toString()
                                                  )}
                                                </Typography>
                                              )}
                                            </Stack>
                                          }
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  </React.Fragment>
                                )
                              })}
                            </List>
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={2}
                            p={2}
                            sx={{
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Total Selected:</strong>{' '}
                                {selectedLineItemIds.size} item
                                {selectedLineItemIds.size === 1 ? '' : 's'}
                              </Typography>
                              {hasQuotedOnlyItems && (
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="primary"
                                >
                                  <strong>Quoted Total:</strong>{' '}
                                  {formatCurrency(totalQuotedCost.toString())}
                                </Typography>
                              )}
                              {hasReconciledItems && (
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="success.main"
                                >
                                  <strong>Reconciled Total:</strong>{' '}
                                  {formatCurrency(totalReconciledCost.toString())}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleCopy}
                              disabled={copying || selectedLineItemIds.size === 0}
                              startIcon={
                                copying ? <CircularProgress size={16} /> : <ContentCopy />
                              }
                            >
                              {copying
                                ? 'Copying...'
                                : `Copy ${selectedLineItemIds.size} Item${selectedLineItemIds.size === 1 ? '' : 's'}`}
                            </Button>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  )
}

export default inject<IStores, ICopyQuoteProps, Partial<ICopyQuoteProps>, any>(
  (stores: IStores) => ({
    quotePotentialMatches: stores.quotes.quotePotentialMatches,
    isLoadingPotentialMatches: stores.quotes.isLoadingPotentialMatches,
    getQuotePotentialMatches: stores.quotes.getQuotePotentialMatches,
    rerouteToPath: stores.global.rerouteToPath,
    loadQuote: stores.quotes.loadQuote,
  })
)(observer(CopyQuote))
